import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, BookMarked, BookOpen } from "lucide-react";

function Exporter({ kind, sources, books, testId }) {
  const [source, setSource] = useState(sources[0]?.key || "");
  const [book, setBook] = useState("");
  const [chapter, setChapter] = useState("all");
  const total = books.find((b) => b.name === book)?.chapters || 0;
  const url = book ? `${API}/admin/export/${kind}/${source}/${encodeURIComponent(book)}${chapter !== "all" ? `/${chapter}` : ""}.pdf` : null;
  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_1fr_140px_auto] sm:items-end" data-testid={testId}>
      <label className="block"><span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{kind === "commentary" ? "Commentary" : "Version"}</span>
        <Select value={source} onValueChange={(v) => setSource(v)}>
          <SelectTrigger className="mt-1 font-serif" data-testid={`${testId}-source`}><SelectValue /></SelectTrigger>
          <SelectContent>{sources.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
        </Select></label>
      <label className="block"><span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Book</span>
        <Select value={book || "none"} onValueChange={(v) => { setBook(v === "none" ? "" : v); setChapter("all"); }}>
          <SelectTrigger className="mt-1 font-serif" data-testid={`${testId}-book`}><SelectValue placeholder="Choose a book" /></SelectTrigger>
          <SelectContent className="max-h-[60vh]"><SelectItem value="none">Choose a book…</SelectItem>{books.map((b) => <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
        </Select></label>
      <label className="block"><span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Chapter</span>
        <Select value={chapter} onValueChange={setChapter} disabled={!book}>
          <SelectTrigger className="mt-1 font-mono text-xs" data-testid={`${testId}-chapter`}><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-[60vh]"><SelectItem value="all">Whole book</SelectItem>{Array.from({ length: total }, (_, i) => i + 1).map((c) => <SelectItem key={c} value={String(c)}>{c}</SelectItem>)}</SelectContent>
        </Select></label>
      <Button asChild={!!url} disabled={!url} className="rounded-full bg-primary text-primary-foreground" data-testid={`${testId}-download`}>
        {url ? <a href={url} target="_blank" rel="noreferrer"><FileDown className="mr-2 h-4 w-4" /> Download PDF</a> : <span><FileDown className="mr-2 h-4 w-4" /> Download PDF</span>}
      </Button>
    </div>
  );
}

export default function AdminExport() {
  const { data: comms } = useQuery({ queryKey: ["admin-commentaries"], queryFn: async () => (await api.get("/admin/commentaries")).data });
  const { data: bibles } = useQuery({ queryKey: ["admin-bible-versions"], queryFn: async () => (await api.get("/admin/bible-versions")).data });
  const { data: books } = useQuery({ queryKey: ["bible-books", "admin"], queryFn: async () => (await api.get("/bible/books")).data });
  if (!comms || !bibles || !books) return <div className="h-40 animate-pulse rounded-2xl bg-muted" />;
  return (
    <div className="space-y-6" data-testid="admin-export">
      <section className="space-y-4 rounded-2xl border border-border bg-card p-5">
        <div className="flex items-start gap-3">
          <BookMarked className="mt-1 h-5 w-5 shrink-0 text-gold" />
          <div>
            <h2 className="font-serif text-2xl font-semibold text-foreground">Commentary PDF</h2>
            <p className="mt-1 text-sm text-muted-foreground">A whole book or a single chapter, with a clickable table of contents, page numbers and a “Contents” link on every page. Each file is built once and cached — later downloads are instant until the commentary data changes.</p>
          </div>
        </div>
        <Exporter kind="commentary" testId="export-commentary" books={books.books} sources={comms.commentaries.map((c) => ({ key: c.slug, label: c.name }))} />
      </section>
      <section className="space-y-4 rounded-2xl border border-border bg-card p-5">
        <div className="flex items-start gap-3">
          <BookOpen className="mt-1 h-5 w-5 shrink-0 text-gold" />
          <div>
            <h2 className="font-serif text-2xl font-semibold text-foreground">Bible PDF</h2>
            <p className="mt-1 text-sm text-muted-foreground">A whole book or one chapter of any Bible version on the site. The NKJV file carries the Thomas Nelson copyright notice.</p>
          </div>
        </div>
        <Exporter kind="bible" testId="export-bible" books={books.books} sources={bibles.versions.map((v) => ({ key: v.code, label: `${v.name} (${v.code})` }))} />
      </section>
    </div>
  );
}
